/*
  Complete your routine — PDP routine upsell.

  Spec: docs/superpowers/specs/2026-08-12-complete-your-routine-design.md

  Two jobs:

  1. Add every step to the cart in one request. This does NOT implement a cart
     path — it calls the theme's own global window.handleAddToCart with exactly
     the signature the two existing bundlers use
     (section-product-bundler.js:61, section-products-set.js:29), so the drawer,
     the 422 stock alert, the header count and the cart recommendations all
     behave identically to every other add on the site.

  2. Keep the total honest while the shopper changes variants. Skin Fluff alone
     spans EUR 21 to 49 across its size axis, so a total computed once at render
     is wrong the moment the size changes. The theme's own product-variants
     element dispatches VARIANT_CHANGE and carries currentVariant, so the lead
     step follows the buy box.

  The money formatter is ported verbatim from component-shipping-notice.js:30 so
  prices re-rendered here are byte-identical to prices rendered by Liquid.
*/

if (typeof CompleteRoutine !== 'function') {
  class CompleteRoutine extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._portal();

      this.config = this._readConfig();
      this.steps = [];
      this.products = new Map();
      this.submit = this.querySelector('[data-routine-submit]');
      this.errorEl = this.querySelector('[data-routine-error]');
      this.stepsHost = this.querySelector('[data-routine-steps]');
      this.stepTemplate = this.querySelector('[data-routine-step-template]');
      this.dividerTemplate = this.querySelector('[data-routine-divider-template]');

      if (!this.submit || !this.stepsHost || !this.stepTemplate) return;

      this.submit.addEventListener('click', () => this._addAll());
      if (this.dataset.hideBundlerWidget === 'true') this._hideBundlerWidget();
      this._hydrateFromBundler();
    }

    /*
      Move the panel into the PDP's info column above 1024px.

      WHY A DOM MOVE AND NOT A SECOND RENDER

      Every dimension, colour and string this panel draws with lives in the
      section's own settings, saved on `complete_routine` in
      templates/product.json. Liquid gives a section no way to read another
      section's settings, so re-rendering the panel from inside main-product
      would silently fall back to schema defaults — right markup, wrong
      colours, wrong copy, wrong discount. Moving the element that Liquid
      already rendered carries all of it across for free.

      WHY THIS IS SAFE TO RE-ENTER

      Moving a custom element fires disconnectedCallback then
      connectedCallback again. The `if (this._init) return` guard at the top
      turns that second call into a no-op, so the move costs no second Bundler
      round-trip and installs no duplicate listeners. The original call
      continues on the line after this one.

      WHY IT CANNOT FLASH

      The element ships with the `hidden` attribute and only drops it once
      Bundler has answered (_hydrateFromBundler). This runs synchronously in
      connectedCallback, long before that, so the panel is never painted in
      the position it is about to leave.

      WHY 1024

      The PDP's two-column grid opens at 1024 (section-main-product.css:1965).
      Below it there is no right-hand column to move into, so the panel stays
      in its own section and renders full width — which is the mobile layout
      it has always had.
    */
    _portal() {
      const column = document.querySelector('.pdp-main-container .pdp-info-column');
      const home = this.parentElement;
      if (!column || !home) return;

      const wide = window.matchMedia('(min-width: 1024px)');

      const place = () => {
        const host = wide.matches ? column : home;
        // appendChild on the current parent would still move the node to the
        // end of its own siblings, which below 1024px is a real reorder.
        if (this.parentElement !== host) host.appendChild(this);
        this.classList.toggle('routine--in-column', wide.matches);
      };

      place();
      wide.addEventListener('change', place);

      /*
        The Theme Editor re-renders a section by replacing its wrapper's
        innerHTML. Above 1024px this element is no longer INSIDE that wrapper,
        so it survives the replacement and the fresh render portals a second
        panel into the column — measured: one panel becomes two on the first
        setting change. Shopify announces the teardown on the wrapper first,
        and that event is the only signal that distinguishes "the editor is
        replacing my section" from "I am being moved by _portal", which fires
        the same disconnectedCallback.

        Scoped by identity (e.target === home) rather than by parsing the
        sectionId out of the wrapper's id, so a second routine section on the
        same page cannot take this one down with it.
      */
      document.addEventListener('shopify:section:unload', (e) => {
        if (e.target !== home) return;
        wide.removeEventListener('change', place);
        this.remove();
      });
    }

    /*
      Hide Bundler's own widget target so the two bundle UIs do not both show.

      The section also ships a plain CSS rule for this, but that rule alone was
      measured NOT to win: with `display: none !important` live in a stylesheet
      and the selector matching, the element still computed `display: block`,
      while the same declaration set inline took effect immediately. Rather than
      keep escalating selector specificity against an app that can change its
      markup, this sets the inline property — the one form that was verified to
      hold — and re-applies it, because Bundler regenerates the element with a
      fresh `_bndl_key_*` id on each load.

      This is display only. The discount is applied server-side from the bundle
      definition (verified: 848 on the full trio, 0 on a partial cart), so
      hiding the widget does not disable Bundler's discounting. Its widget-click
      analytics are the only thing lost.
    */
    _hideBundlerWidget() {
      const hide = () => {
        document.querySelectorAll('.bundler-target-element, .bndlr-bundle-container').forEach((el) => {
          if (el.style.getPropertyValue('display') !== 'none') {
            el.style.setProperty('display', 'none', 'important');
          }
        });
      };
      hide();
      const observer = new MutationObserver(hide);
      observer.observe(document.body, { childList: true, subtree: true });
      // Bundler renders late; stop watching once the page has settled so this
      // is not an observer running for the life of the session.
      setTimeout(() => observer.disconnect(), 15000);
    }

    /* ---------------------------------------------------------------------
       Bundler adapter

       Division of labour: Bundler owns the bundle definition and the checkout
       discount engine; this section owns presentation, variant selection and
       the cart add. So the percentage shown here is READ from Bundler rather
       than configured twice — the schema's discount_percent is only the value
       Liquid server-rendered, used until Bundler answers and as the permanent
       fallback if it never does.

       getBundles() is browser-side data, so the section renders with the Liquid
       figure first and re-renders on hydration. Every field below is read
       defensively: bundler.app documents the METHODS but not the returned
       object's schema, so a shape change must degrade to the fallback rather
       than throw. Checkout remains Bundler's authority either way — this only
       decides what number the shopper is shown beforehand.
    --------------------------------------------------------------------- */

    async _hydrateFromBundler() {
      try {
        const bndlr = await this._waitForBundler();
        if (!bndlr) return this._stayHidden('no-app');

        const bundle = this._pickBundle(await bndlr.getBundles());
        if (!bundle) return this._stayHidden('no-bundle');

        const catalogue = await this._waitForCatalogue(bndlr, this._bundleProductIds(bundle));
        if (!catalogue) return this._stayHidden('no-catalogue');

        // Copy is fetched from each product's own page, in parallel, before
        // the cards are built — see _fetchCopy.
        this.copy = await this._fetchCopy(catalogue, this._bundleProductIds(bundle));

        if (this._buildSteps(bundle, catalogue) < 2) return this._stayHidden('too-few-products');

        this.bundlerDiscount = this._readDiscount(bundle);
        this.dataset.discountSource = this.bundlerDiscount ? 'bundler' : 'setting';
        this.dataset.bundlerBundleId = String(bundle.id ?? '');

        this._writeCopy();
        this._watchMainVariant();
        this._render();
        this.dataset.routineState = 'ready';
        this.hidden = false;
      } catch (e) {
        this._stayHidden('error');
      }
    }

    /*
      The routine IS the bundle, so with no bundle there is nothing truthful to
      show — the section stays hidden rather than rendering an empty shell or a
      routine it cannot price. The reason is left on the element for debugging.
    */
    _stayHidden(reason) {
      this.dataset.routineState = reason;
      this.hidden = true;
    }

    /* bundler:loaded is the documented ready event. Poll as well, because the
       app may already have fired it before this element upgraded. */
    _waitForBundler(timeout = 8000) {
      if (window.bndlr && typeof window.bndlr.getBundles === 'function') {
        return Promise.resolve(window.bndlr);
      }
      return new Promise((resolve) => {
        let done = false;
        const finish = (v) => {
          if (done) return;
          done = true;
          clearInterval(poll);
          clearTimeout(bail);
          document.removeEventListener('bundler:loaded', onLoaded);
          resolve(v);
        };
        const ready = () =>
          window.bndlr && typeof window.bndlr.getBundles === 'function' ? window.bndlr : null;
        const onLoaded = () => finish(ready());
        document.addEventListener('bundler:loaded', onLoaded);
        const poll = setInterval(() => {
          const b = ready();
          if (b) finish(b);
        }, 250);
        const bail = setTimeout(() => finish(null), timeout);
      });
    }

    /*
      Bundler populates in two stages: `getBundles()` answers roughly 300ms
      before `getProductConfig()` has anything in it. Measured on this shop —
      getBundles returned 3 bundles at 322ms while getProductConfig was still
      `{}`, filling in about a second later. Treating "the method exists" as
      "the data is here" built zero cards and hid the section entirely.

      So wait for the catalogue to actually contain the bundle's products, not
      merely for the API to be callable.
    */
    async _waitForCatalogue(bndlr, wanted, timeout = 8000) {
      const deadline = Date.now() + timeout;
      const have = (cfg) => (cfg ? wanted.filter((id) => cfg[id]).length : 0);
      let last = null;

      // Hold out for the WHOLE bundle. Accepting a partial catalogue renders a
      // short routine at a wrong total — measured live as a 2-of-3 routine
      // priced at 49,00 instead of 84,75, which is worse than not rendering.
      while (Date.now() < deadline) {
        try {
          last = await bndlr.getProductConfig();
        } catch (e) {
          last = null;
        }
        if (have(last) === wanted.length) return last;
        await new Promise((r) => setTimeout(r, 120));
      }

      // Out of time. Two products is still a routine; one is not.
      return have(last) >= 2 ? last : null;
    }

    /*
      Ask each product's own page for its copy.

      Liquid cannot supply this up front: the routine comes from Bundler at
      runtime, so at render time the section does not know which products it
      will describe, and a product metafield is unreachable without the handle.
      Bundler's catalogue DOES carry handles, so once it answers, each product's
      page can be asked for its own metafields via the section rendering API.

      Nothing is duplicated into the theme as a result — the cards read the
      subtitle the shop already maintains. Failures are per-product and silent;
      a missing payload just means that card falls back to its Shopify title.
    */
    async _fetchCopy(catalogue, ids) {
      const map = {};
      await Promise.all(
        ids.map(async (id) => {
          const handle = catalogue[id] && catalogue[id].handle;
          if (!handle) return;
          try {
            const res = await fetch(
              `/products/${encodeURIComponent(handle)}?section_id=routine-copy`
            );
            if (!res.ok) return;
            const text = await res.text();
            // The section renders bare JSON, but Shopify may wrap it in a
            // container div, so take the outermost braces.
            const start = text.indexOf('{');
            const end = text.lastIndexOf('}');
            if (start === -1 || end <= start) return;
            map[id] = JSON.parse(text.slice(start, end + 1));
          } catch (e) {
            /* leave this product without copy */
          }
        })
      );
      return map;
    }

    /* Which field fills each slot, per the section's settings. */
    _copyFor(id, product) {
      const c = (this.copy && this.copy[id]) || {};
      const pick = (key) => (key && c[key] ? String(c[key]).trim() : '');
      return {
        name: pick(this.config.nameSource) || c.title || product.title || '',
        desc: this.config.descSource === 'none' ? '' : pick(this.config.descSource),
      };
    }

    /* Bundle product ids in the merchant's configured order. */
    _bundleProductIds(bundle) {
      return Object.values(bundle.products || {})
        .slice()
        .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
        .map((p) => String((p && (p.id ?? p.product_id)) || ''))
        .filter(Boolean);
    }

    /*
      Pick the bundle this page belongs to: enabled, not volume/quantity-break
      (those discount on count rather than on owning a specific set), and
      containing the product being viewed. Ties break on `priority` — Bundler
      does not document which direction that sorts, so it is only ever a
      tiebreak, never the primary test.
    */
    _pickBundle(bundles) {
      const list = Array.isArray(bundles) ? bundles : Object.values(bundles || {});
      const current = String(this.dataset.currentProduct || '');
      if (!current) return null;

      const candidates = list
        .filter((b) => b && String(b.status ?? 'enabled') === 'enabled')
        .filter((b) => String(b.is_volume_bundle ?? 'false') !== 'true')
        .filter((b) => String(b.is_quantity_break ?? 'false') !== 'true')
        .map((b) => ({ bundle: b, ids: this._bundleProductIds(b) }))
        .filter(({ ids }) => ids.length >= 2 && ids.includes(current));

      if (!candidates.length) return null;
      candidates.sort(
        (a, b) => (Number(a.bundle.priority) || 0) - (Number(b.bundle.priority) || 0)
      );
      return candidates[0].bundle;
    }

    /* ---------------------------------------------------------------------
       Building the steps

       Cards are cloned from the Liquid template so every class the stylesheet
       targets stays in the section file; nothing here writes markup.
    --------------------------------------------------------------------- */

    _buildSteps(bundle, catalogue) {
      const current = String(this.dataset.currentProduct || '');
      const ordered = this._bundleProductIds(bundle);
      // The product being viewed leads; the rest keep the bundle's own order.
      const ids = ordered
        .filter((id) => id === current)
        .concat(ordered.filter((id) => id !== current));
      const hasLead = ids[0] === current;

      this.stepsHost.textContent = '';
      this.steps = [];
      this.products.clear();
      this.dividerEl = null;

      ids.forEach((id) => {
        const product = catalogue[id];
        if (!product) return;
        const isLead = id === current;
        const variant = this._pickVariant(product, isLead);
        if (!variant) return;

        if (this.steps.length === 1 && hasLead) this._insertDivider();
        this.products.set(String(id), product);
        const el = this._stepEl(product, variant, id, isLead);
        this.stepsHost.appendChild(el);
        this.steps.push(el);
      });

      this._setDesktopPositions();

      return this.steps.length;
    }

    /* Mobile deliberately keeps the selected PDP product first. The desktop
       reference puts that selection in the centre, with the first remaining
       ritual step before it and the last after it. `order` only participates
       once the desktop steps host becomes a grid, so this metadata cannot
       alter the mobile DOM flow or divider position. */
    _setDesktopPositions() {
      const lead = this.steps.find((el) => el.hasAttribute('data-routine-lead'));
      const others = this.steps.filter((el) => el !== lead);
      const arranged = lead && others.length ? [others[0], lead, ...others.slice(1)] : this.steps;

      arranged.forEach((el, index) => {
        el.style.setProperty('--routine-desktop-order', String(index + 1));
        el.toggleAttribute('data-routine-desktop-connector', index < arranged.length - 1);
      });
    }

    _insertDivider() {
      if (!this.dividerTemplate || !this.config.dividerLabel) return;
      const frag = this.dividerTemplate.content.cloneNode(true);
      this.dividerEl = frag.querySelector('[data-routine-divider-label]');
      this.stepsHost.appendChild(frag);
    }

    /* The lead step follows the buy box; the rest take the first sellable
       variant, which is what the shopper would get from the product page. */
    _pickVariant(product, isLead) {
      const variants = product.variants || [];
      if (isLead) {
        const live = this._liveVariantId();
        const found = live && variants.find((v) => String(v.id) === String(live));
        if (found) return found;
      }
      return variants.find((v) => v.available) || variants[0] || null;
    }

    _liveVariantId() {
      const picker = document.querySelector('product-variants[data-main-product-variants]');
      if (picker && picker.currentVariant) return picker.currentVariant.id;
      const input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      return input ? input.value : null;
    }

    _stepEl(product, variant, id, isLead) {
      const el = this.stepTemplate.content.cloneNode(true).querySelector('[data-routine-step]');
      const copy = this._copyFor(id, product);
      const name = copy.name;

      el.dataset.productId = id;
      this._writeVariant(el, variant);
      if (isLead) el.setAttribute('data-routine-lead', '');

      el.querySelector('[data-routine-name]').textContent = name;

      const desc = el.querySelector('[data-routine-desc]');
      if (copy.desc) {
        desc.textContent = copy.desc;
        desc.hidden = false;
      }

      this._writeStepImage(el, product, variant, name);
      this._buildVariantControl(el, product, variant, name);

      const badge = el.querySelector('[data-routine-badge]');
      const badgeText = el.querySelector('[data-routine-badge-text]');
      if (isLead && this.config.badgeLabel && badge && badgeText) {
        badgeText.textContent = this.config.badgeLabel;
        badge.hidden = false;
      }

      return el;
    }

    _writeStepImage(el, product, variant, name) {
      const img = el.querySelector('[data-routine-img]');
      if (!img) return;

      const src =
        (variant.featured_image && variant.featured_image.src) ||
        product.featured_image ||
        (product.images || [])[0];
      if (!src) return;

      const mobile = parseInt(this.config.mediaWidth, 10) || 144;
      const desktop = 560;
      img.src = this._cdn(src, mobile * 2);
      img.srcset = [mobile, mobile * 2, desktop]
        .filter((width, index, widths) => widths.indexOf(width) === index)
        .map((width) => `${this._cdn(src, width)} ${width}w`)
        .join(', ');
      img.sizes = `(min-width: 769px) 280px, ${mobile}px`;
      img.alt = name;
    }

    _buildVariantControl(el, product, selected, name) {
      const control = el.querySelector('[data-routine-variant-control]');
      const select = el.querySelector('[data-routine-variant-select]');
      const label = el.querySelector('[data-routine-variant-label]');
      const variants = product.variants || [];
      const meaningful = variants.filter((variant) =>
        String(variant.title || '').trim() && String(variant.title).trim() !== 'Default Title'
      );

      if (!control || !select || meaningful.length <= 1) return;

      select.textContent = '';
      meaningful.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.title;
        option.disabled = variant.available === false;
        option.selected = String(variant.id) === String(selected.id);
        select.appendChild(option);
      });

      if (label) label.textContent = `Choose a variant for ${name}`;
      select.setAttribute('aria-label', `Choose a variant for ${name}`);
      control.hidden = false;

      select.addEventListener('change', () => {
        const variant = variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        this._writeVariant(el, variant);
        this._writeStepImage(el, product, variant, name);
        this._render();
      });
    }

    _writeVariant(el, variant) {
      el.dataset.variantId = variant.id;
      el.dataset.price = variant.price;
      el.dataset.compare =
        variant.compare_at_price && variant.compare_at_price > variant.price
          ? variant.compare_at_price
          : variant.price;

      const select = el.querySelector('[data-routine-variant-select]');
      if (select && [...select.options].some((option) => option.value === String(variant.id))) {
        select.value = String(variant.id);
      }
    }

    /* Shopify CDN urls are protocol-relative and take a width parameter. */
    _cdn(url, width) {
      const abs = String(url).startsWith('//') ? `https:${url}` : String(url);
      return `${abs}${abs.includes('?') ? '&' : '?'}width=${width}`;
    }

    /* Intro and divider copy name the products, so they can only be written
       once the steps exist. */
    _writeCopy() {
      const names = this.steps.map((el) =>
        el.querySelector('[data-routine-name]').textContent.trim()
      );
      const lead = this.querySelector('[data-routine-lead]');
      const leadName = lead ? names[0] : '';
      const others = lead ? names.slice(1) : names;

      const introEl = this.querySelector('[data-routine-intro]');
      if (introEl && this.config.intro) {
        const text = String(this.config.intro)
          .replace(/\[current\]/g, leadName)
          .replace(/\[others\]/g, this._joinNames(others));
        introEl.textContent = text;
        introEl.hidden = false;
      }

      if (this.dividerEl) {
        // [s] is an explicit plural marker rather than an English rule baked
        // into the code: a two-product bundle leaves one step, and "add these
        // one steps" is what a naive count substitution produces.
        this.dividerEl.textContent = String(this.config.dividerLabel)
          .replace(/\[count\]/g, this._numberWord(others.length))
          .replace(/\[s\]/g, others.length === 1 ? '' : 's');
      }
    }

    _joinNames(list) {
      if (list.length <= 1) return list[0] || '';
      return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
    }

    _numberWord(n) {
      return ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'][n] || String(n);
    }

    /* Normalise Bundler's three discount shapes into one. Values arrive as
       strings and in major currency units; everything here works in cents. */
    _readDiscount(bundle) {
      const type = String(bundle.discount_type || '');
      const num = (v) => {
        const n = parseFloat(String(v).replace(',', '.'));
        return Number.isFinite(n) ? n : null;
      };

      if (type === 'percentage') {
        const pct = num(bundle.percentage_value);
        return pct !== null && pct > 0 && pct < 100 ? { kind: 'percentage', value: pct } : null;
      }
      if (type === 'fixed_amount') {
        const off = num(bundle.fixed_amount_value);
        return off !== null && off > 0 ? { kind: 'amount_off', value: Math.round(off * 100) } : null;
      }
      if (type === 'fixed_price') {
        const price = num(bundle.fixed_price_value);
        return price !== null && price > 0
          ? { kind: 'fixed_price', value: Math.round(price * 100) }
          : null;
      }
      return null;
    }

    _readConfig() {
      const tag = this.querySelector('[data-routine-config]');
      const fallback = {
        fallbackDiscount: 0,
        saveLabel: 'You save [amount]',
        intro: '',
        dividerLabel: '',
        badgeLabel: '',
        mediaWidth: 144,
        nameSource: 'title',
        descSource: 'subtitle',
      };
      if (!tag) return fallback;
      try {
        return { ...fallback, ...JSON.parse(tag.textContent) };
      } catch (e) {
        return fallback;
      }
    }

    /*
      The lead step is the product this page is about, so its price has to track
      the buy box rather than the value Liquid rendered. Everything else is
      fixed at its first available variant, which is what the merchant picked.
    */
    _watchMainVariant() {
      const lead = this.querySelector('[data-routine-lead]');
      if (!lead) return;

      const picker =
        document.querySelector('product-variants[data-main-product-variants]') ||
        document.querySelector('product-variants[data-main-product-page-variants]');
      if (!picker) return;

      picker.addEventListener('VARIANT_CHANGE', (e) => {
        const variant = e.target.currentVariant;
        if (!variant) {
          // An unavailable combination. Keep the last good price but block the add.
          lead.dataset.variantId = '';
          this._render();
          return;
        }
        this._writeVariant(lead, variant);
        const product = this.products.get(String(lead.dataset.productId));
        const name = lead.querySelector('[data-routine-name]')?.textContent.trim() || '';
        if (product) this._writeStepImage(lead, product, variant, name);
        this._render();
      });
    }

    /*
      Bundler's discount wins once it has answered; until then (and forever, if
      the app never loads) this reproduces exactly what Liquid rendered, so the
      figure never changes for no reason.
    */
    get _totals() {
      const compare = this.steps.reduce(
        (sum, el) => sum + (parseInt(el.dataset.compare, 10) || 0),
        0
      );

      let total;
      const d = this.bundlerDiscount;
      if (d && d.kind === 'percentage') {
        total = Math.floor((compare * (100 - d.value)) / 100);
      } else if (d && d.kind === 'amount_off') {
        total = Math.max(0, compare - d.value);
      } else if (d && d.kind === 'fixed_price') {
        total = d.value;
      } else {
        const keep = 100 - (parseInt(this.config.fallbackDiscount, 10) || 0);
        // Integer cents, matching Liquid's truncating division exactly.
        total = Math.floor((compare * keep) / 100);
      }

      return { compare, total, savings: Math.max(0, compare - total) };
    }

    _render() {
      const { compare, total, savings } = this._totals;

      const totalEl = this.querySelector('[data-routine-total]');
      const compareEl = this.querySelector('[data-routine-compare]');
      const saveEl = this.querySelector('[data-routine-save]');

      if (totalEl) totalEl.textContent = this._money(total);

      if (compareEl) {
        compareEl.hidden = savings <= 0;
        if (savings > 0) compareEl.textContent = this._money(compare);
      }

      if (saveEl) {
        saveEl.hidden = savings <= 0;
        if (savings > 0) {
          saveEl.textContent = String(this.config.saveLabel).replace(
            /\[amount\]/g,
            this._money(savings)
          );
        }
      }

      this.querySelectorAll('[data-routine-card-total]').forEach((el) => {
        el.textContent = this._money(total);
      });

      this.querySelectorAll('[data-routine-card-compare]').forEach((el) => {
        el.hidden = savings <= 0;
        if (savings > 0) el.textContent = this._money(compare);
      });

      // A step with no resolvable variant cannot be added, so nothing can.
      const ready = this.steps.every((el) => el.dataset.variantId);
      this.submit.disabled = !ready;
    }

    _addAll() {
      if (this.submit.disabled || this.submit.classList.contains('working')) return;

      const items = [];
      this.steps.forEach((el) => {
        const id = el.dataset.variantId;
        if (!id) return;
        const existing = items.find((item) => String(item.id) === String(id));
        if (existing) {
          existing.quantity++;
        } else {
          items.push({ id: id, quantity: 1 });
        }
      });

      if (!items.length) return;

      if (typeof window.handleAddToCart !== 'function') {
        this._error('Could not reach the cart. Please refresh and try again.');
        return;
      }

      this._error('');
      this.submit.classList.add('working');

      window.handleAddToCart(
        JSON.stringify({ items: items }),
        items.reduce((total, { quantity }) => total + quantity, 0),
        { 'Content-Type': 'application/json' },
        () => {
          this.submit.classList.remove('working');

          /*
            Bundler's own bndlr:bundle_added_to_cart fires only for adds made
            through its widget, so it will NOT fire here and widget-attributed
            analytics will not count these. That event is deliberately not
            forged — faking an app's internal event would corrupt its reporting.
            This is the section's own signal instead; bind analytics to it.
          */
          this.dispatchEvent(
            new CustomEvent('routine:added', {
              bubbles: true,
              detail: {
                items: items,
                bundleId: this.dataset.bundlerBundleId || null,
                discountSource: this.dataset.discountSource || 'setting',
                total: this._totals.total,
                savings: this._totals.savings,
              },
            })
          );
        }
      );
    }

    _error(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.toggle('is-shown', Boolean(message));
    }

    /* Ported from assets/component-shipping-notice.js:30. */
    _money(cents) {
      if (typeof cents === 'string') cents = cents.replace('.', '');

      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString =
        (window.KROWN && KROWN.settings && KROWN.settings.shop_money_format) ||
        '${{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) return 0;

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        const iso = (window.KROWN && KROWN.settings && KROWN.settings.iso_code) || '';
        return dollarsAmount + centsAmount + iso;
      }

      const match = formatString.match(placeholderRegex);
      if (!match) return formatString;

      let value = '';
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }
  }

  if (typeof customElements.get('complete-routine') === 'undefined') {
    customElements.define('complete-routine', CompleteRoutine);
  }
}
